'use client'

import { useCallback, useEffect, useState } from 'react'
import { apiFetch } from '@/lib/api'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { GlassCard, GlassCardHeader, GlassCardTitle } from '@/components/ui/glass-card'
import { SectionHeader } from '@/components/ui/section-header'
import { Settings, Users, Loader2, UserCheck } from 'lucide-react'
import { PerfSettings } from './PerfSettings'
import { PageShell } from '@/components/ui/page-shell'
import { TabLoader, EmptyState } from '@/components/ui/loading'
import { PRIMARY_BTN } from '@/components/ui/button-styles'
import { cn } from '@/lib/utils'

export function SettingsView() {
  const [inactiveUsers, setInactiveUsers] = useState<any[]>([])
  const [loadingUsers, setLoadingUsers] = useState<boolean>(true)
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null)

  const fetchInactiveUsers = useCallback(async () => {
    setLoadingUsers(true)
    try {
      const res = await apiFetch('/api/users')
      const data = await res.json()
      setInactiveUsers((data.users || []).filter((u: any) => u.isActive === false))
    } catch (e: any) {
      console.error('Error fetching inactive users:', e)
      toast.error(e.message || 'Failed to load users')
    } finally {
      setLoadingUsers(false)
    }
  }, [])

  useEffect(() => {
    fetchInactiveUsers()
  }, [fetchInactiveUsers])

  const activateUser = async (employee: any) => {
    setUpdatingUserId(employee.id)
    try {
      await apiFetch(`/api/users/${employee.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: true }),
      })
      setInactiveUsers(current => current.filter(user => user.id !== employee.id))
    } catch (e: any) {
      console.error('Error activating employee:', e)
      toast.error(e.message || 'Could not activate employee')
    } finally {
      setUpdatingUserId(null)
    }
  }

  return (
    <PageShell>
      <SectionHeader icon={Settings} title="Settings" description="Global application and configuration settings" />
      
      <div className="space-y-6">
        <GlassCard>
          <GlassCardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/10 text-rose-500">
                <Users className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <GlassCardTitle>Inactive Employees</GlassCardTitle>
                <p className="text-xs text-neutral-400 dark:text-white/40 mt-1">
                  Deactivated employees are excluded from active lists, attendance tools, and stipend calculations. Reactivate an employee here when they return.
                </p>
              </div>
            </div>
          </GlassCardHeader>
          <div className="p-5">
            {loadingUsers ? (
              <TabLoader label="Loading employees" />
            ) : inactiveUsers.length === 0 ? (
              <EmptyState
                icon={UserCheck}
                title="No inactive employees"
                description="Deactivated employees will appear here so you can reactivate them."
              />
            ) : (
              <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto rounded-xl border border-neutral-200 dark:border-white/5 bg-neutral-50 dark:bg-white/[0.01]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-neutral-200 dark:border-white/5 bg-neutral-100 dark:bg-neutral-950/40 text-neutral-500 dark:text-white/40 text-xs text-left">
                      <th className="p-3 font-semibold">Employee ID</th>
                      <th className="p-3 font-semibold">Name</th>
                      <th className="p-3 font-semibold">Email</th>
                      <th className="p-3 font-semibold">Department</th>
                       <th className="p-3 font-semibold">Status</th>
                       <th className="p-3 font-semibold text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inactiveUsers.map(user => (
                      <tr key={user.id} className="border-b border-neutral-100 dark:border-white/5 text-neutral-800 dark:text-white/80 hover:bg-neutral-50 dark:hover:bg-white/[0.02]">
                        <td className="p-3 font-mono text-xs">{user.employeeId || '-'}</td>
                        <td className="p-3 font-medium">{user.name}</td>
                        <td className="p-3">{user.email}</td>
                        <td className="p-3">{user.department || '-'}</td>
                        <td className="p-3">
                          <span className="inline-flex items-center rounded-md bg-rose-400/10 px-2 py-1 text-xs font-medium text-rose-400 ring-1 ring-inset ring-rose-400/20">
                            Inactive
                          </span>
                        </td>
                        <td className="p-3 text-right">
                          <Button size="sm" className={PRIMARY_BTN} onClick={() => activateUser(user)} disabled={updatingUserId === user.id}>
                            {updatingUserId === user.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserCheck className="mr-1 h-4 w-4" />Activate</>}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile card list — this table has 6 columns and cannot scroll
                  usefully on a phone, so it collapses to stacked cards. */}
              <div className="grid gap-3 md:hidden">
                {inactiveUsers.map(user => (
                  <div
                    key={user.id}
                    className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 dark:border-white/5 dark:bg-white/[0.01]"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate font-medium text-neutral-900 dark:text-white/90">{user.name}</p>
                        <p className="truncate text-xs text-neutral-500 dark:text-white/40">{user.email}</p>
                      </div>
                      <span className="shrink-0 inline-flex items-center rounded-md bg-rose-400/10 px-2 py-1 text-xs font-medium text-rose-500 ring-1 ring-inset ring-rose-400/20 dark:text-rose-400">
                        Inactive
                      </span>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <p className="text-neutral-400 dark:text-white/40">Employee ID</p>
                        <p className="font-mono text-neutral-800 dark:text-white/80">{user.employeeId || '-'}</p>
                      </div>
                      <div>
                        <p className="text-neutral-400 dark:text-white/40">Department</p>
                        <p className="text-neutral-800 dark:text-white/80">{user.department || '-'}</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      className={cn(PRIMARY_BTN, 'mt-3 w-full')}
                      onClick={() => activateUser(user)}
                      disabled={updatingUserId === user.id}
                    >
                      {updatingUserId === user.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserCheck className="mr-1 h-4 w-4" />Activate</>}
                    </Button>
                  </div>
                ))}
              </div>
              </>
            )}
          </div>
        </GlassCard>

        <PerfSettings />
      </div>
    </PageShell>
  )
}
